<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from 'vue';
import { useBlockSafe } from '@/composables/useBlockSafe';
import { useRequest } from '@/composables/useRequest';
const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});
const {
  id,
  backgroundColor,
  spacingTop,
  spacingBottom,
  title,
  hasTitle,
  brands,
  hasBrands,
  hasAnyContent,
} = useBlockSafe(props.data);

const selectBackgroundColor = computed(() => {
  const colorMap = {
    white: 'gray',
    gray: 'white',
    black: 'black',
  };

  return colorMap[backgroundColor.value] || backgroundColor.value;
});

const selectedBrand = ref(null);
const selectedModel = ref(null);
const selectedEngine = ref(null);
const maintenanceType = ref('number');
const selectedMaintenanceNumber = ref(null);
const mileageValue = ref(null);
const showError = ref(false);

const models = ref([]);
const engines = ref([]);
const maintenanceNumbers = ref([]);

const calculationResult = ref(null);

//const loadingBrands = ref(false)
const loadingModels = ref(false);
const loadingEngines = ref(false);
// Shared across all cascading handlers so a newer selection always invalidates
// any still in-flight response from a previous one, preventing stale data
// (and the mismatched brand/model/engine params that follow) from being applied.
const requestSeq = ref(0);

// Validation
const isFormValid = computed(() => {
  const basicsValid =
    selectedBrand.value && selectedModel.value && selectedEngine.value;
  const maintenanceValid =
    (maintenanceType.value === 'number' && selectedMaintenanceNumber.value) ||
    (maintenanceType.value === 'mileage' && mileageValue.value);

  return basicsValid && maintenanceValid;
});

// When brand changes: Fetch models
const onBrandChange = async () => {
  const seq = ++requestSeq.value;
  selectedModel.value = null;
  selectedEngine.value = null;
  engines.value = [];
  models.value = [];
  maintenanceType.value = 'number';
  selectedMaintenanceNumber.value = null;
  maintenanceNumbers.value = [];
  mileageValue.value = null;
  calculationResult.value = null;
  if (!selectedBrand.value) return;

  try {
    loadingModels.value = true;
    const response = await useRequest(
      `/calculator?type=to_schedule&brand_id=${selectedBrand.value}`,
    );
    if (seq !== requestSeq.value) return;
    models.value = response.data || [];
  } catch (error) {
    console.error('Error fetching models:', error);
  } finally {
    if (seq === requestSeq.value) loadingModels.value = false;
  }
};

// When model changes: Fetch engines
const onModelChange = async () => {
  const seq = ++requestSeq.value;
  selectedEngine.value = null;
  maintenanceType.value = 'number';
  selectedMaintenanceNumber.value = null;
  maintenanceNumbers.value = [];
  mileageValue.value = null;
  calculationResult.value = null;
  if (!selectedBrand.value || !selectedModel.value) return;

  try {
    loadingEngines.value = true;
    const response = await useRequest(
      `/calculator?type=to_schedule&brand_id=${selectedBrand.value}&car_id=${selectedModel.value}`,
    );
    if (seq !== requestSeq.value) return;
    engines.value = response.data || [];
  } catch (error) {
    console.error('Error fetching engines:', error);
  } finally {
    if (seq === requestSeq.value) loadingEngines.value = false;
  }
};
// When engine changes: Fetch maintenance numbers
const onEngineChange = async () => {
  const seq = ++requestSeq.value;
  selectedMaintenanceNumber.value = null;
  maintenanceType.value = 'number';
  selectedMaintenanceNumber.value = null;
  maintenanceNumbers.value = [];
  mileageValue.value = null;
  calculationResult.value = null;
  if (!selectedBrand.value || !selectedModel.value || !selectedEngine.value)
    return;

  try {
    const response = await useRequest(
      `/calculator?type=to_schedule&brand_id=${selectedBrand.value}&car_id=${selectedModel.value}&configuration_id=${selectedEngine.value}`,
    );
    if (seq !== requestSeq.value) return;
    maintenanceNumbers.value = response.data || [];
  } catch (error) {
    console.error('Error fetching maintenance numbers:', error);
  }
};

const calculateMaintenance = async () => {
  showError.value = false;

  if (!isFormValid.value) {
    showError.value = true;
    return;
  }
  const params = new URLSearchParams({
    type: 'to_schedule',
    brand_id: selectedBrand.value,
    car_id: selectedModel.value,
    configuration_id: selectedEngine.value,
  });

  if (maintenanceType.value === 'number') {
    params.append('number', selectedMaintenanceNumber.value);
  } else {
    params.append('mileage', mileageValue.value);
  }
  try {
    const response = await useRequest(`/calculator?${params.toString()}`);
    if (response && (response.success || response.data)) {
      calculationResult.value = {
        image: response.image || {
          alt: 'Maintenance',
          height: 138,
          src: '/images/style/calculator-car.png',
          width: 285,
        },
        brand_name: response.brand_name || '',
        model_name: response.model_name || '',
        pricing: {
          price: response.data?.price || 0,
          currency: response.pricing?.currency || '',
        },
      };
    } else {
      console.warn('API returned success:false or no data');
      calculationResult.value = null;
    }
  } catch (error) {
    console.error('Error calculating maintenance:', error);
    calculationResult.value = null;
  }
};

const getTitleById = (list, id) => {
  if (!list || !id) return '';
  const item = list.find((item) => item.id === id);
  return item ? item.title : '';
};

const formatPrice = (price) => {
  return price.toLocaleString('uk-UA');
};
</script>
<template>
  <section
    v-if="hasAnyContent"
    :id="id"
    class="section"
    :class="[
      backgroundColor ? `bg-${backgroundColor}` : '',
      backgroundColor === 'black' ? 'white' : 'black',
      spacingTop ? `section--top-${spacingTop}` : 'section--top-xl',
      spacingBottom ? `section--bottom-${spacingBottom}` : 'section--bottom-xl',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div class="container">
      <div class="maintenance-calculator">
        <h2
          v-if="hasTitle"
          class="mb-0 maintenance-calculator__title h2 text-center"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
          v-html="title"
        ></h2>
        <div
          v-if="hasBrands"
          :class="[
            `maintenance-calculator__wrap maintenance-calculator__wrap--${backgroundColor}`,
          ]"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
        >
          <form class="maintenance-calculator__form">
            <p class="m-0 h4 maintenance-calculator__form-title">
              {{ $t('maintenance_calculator.enter_data') }}
            </p>
            <div class="maintenance-calculator__form-group">
              <div
                class="maintenance-calculator__form-item maintenance-calculator__form-item--brand"
              >
                <FloatLabel
                  variant="on"
                  :class="[
                    `maintenance-calculator__field floatlabel-on${selectBackgroundColor}`,
                  ]"
                >
                  <Select
                    v-model="selectedBrand"
                    :options="brands"
                    option-label="title"
                    option-value="id"
                    :placeholder="$t('maintenance_calculator.select_brand')"
                    input-id="selectedBrand"
                    :class="[
                      `
                      select-calculator
                      select-on${selectBackgroundColor}
                      p-inputwrapper-focus
                      maintenance-calculator__select
                    `,
                    ]"
                    variant="filled"
                    :empty-message="' '"
                    @change="onBrandChange"
                  />
                  <label for="selectedBrand">{{
                    $t('maintenance_calculator.brand_label')
                  }}</label>
                </FloatLabel>
              </div>
              <div
                class="maintenance-calculator__form-item maintenance-calculator__form-item--model"
              >
                <FloatLabel
                  variant="on"
                  :class="[
                    `maintenance-calculator__field floatlabel-on${selectBackgroundColor}`,
                  ]"
                >
                  <Select
                    v-model="selectedModel"
                    :options="models"
                    option-label="title"
                    option-value="id"
                    :placeholder="$t('maintenance_calculator.select_model')"
                    input-id="model"
                    variant="filled"
                    :empty-message="' '"
                    :class="[
                      `
                      select-calculator
                      select-on${selectBackgroundColor}
                      p-inputwrapper-focus
                      maintenance-calculator__select
                    `,
                    ]"
                    :disabled="models.length === 0 || loadingModels"
                    @change="onModelChange"
                  />
                  <label for="model">{{
                    $t('maintenance_calculator.model_label')
                  }}</label>
                </FloatLabel>
              </div>
              <div class="maintenance-calculator__form-item">
                <FloatLabel
                  variant="on"
                  :class="[
                    `maintenance-calculator__field floatlabel-on${selectBackgroundColor}`,
                  ]"
                >
                  <Select
                    v-model="selectedEngine"
                    :options="engines"
                    option-label="title"
                    option-value="id"
                    :placeholder="$t('maintenance_calculator.select_engine')"
                    input-id="engine"
                    variant="filled"
                    :empty-message="' '"
                    :class="[
                      `
                      select-calculator
                      select-on${selectBackgroundColor}
                      p-inputwrapper-focus
                      maintenance-calculator__select
                    `,
                    ]"
                    :disabled="
                      (!selectedModel && engines.length === 0) || loadingEngines
                    "
                    @change="onEngineChange"
                  />
                  <label for="engine">{{
                    $t('maintenance_calculator.engine_label')
                  }}</label>
                </FloatLabel>
              </div>
              <div
                class="maintenance-calculator__form-item maintenance-calculator__form-item--radio"
              >
                <div class="maintenance-calculator__radio">
                  <RadioButton
                    v-model="maintenanceType"
                    name="maintenance"
                    value="number"
                    input-id="maintenanceNumber"
                    :class="[
                      `radio-on${selectBackgroundColor} maintenance-calculator__radio-button`,
                    ]"
                    :disabled="!selectedEngine"
                  />
                  <label
                    for="maintenanceNumber"
                    class="maintenance-calculator__radio-label body-3-regular"
                  >
                    {{ $t('maintenance_calculator.maintenance_number') }}
                  </label>
                </div>

                <div class="maintenance-calculator__radio">
                  <RadioButton
                    v-model="maintenanceType"
                    name="maintenance"
                    value="mileage"
                    input-id="maintenanceMileage"
                    :class="[
                      `radio-on${selectBackgroundColor} maintenance-calculator__radio-button`,
                    ]"
                    :disabled="!selectedEngine"
                  />
                  <label
                    for="maintenanceMileage"
                    class="maintenance-calculator__radio-label body-3-regular"
                  >
                    {{ $t('maintenance_calculator.maintenance_mileage') }}
                  </label>
                </div>
              </div>
              <div
                v-if="maintenanceType === 'number'"
                class="maintenance-calculator__form-item maintenance-calculator__form-item--last"
              >
                <Select
                  v-model="selectedMaintenanceNumber"
                  :options="maintenanceNumbers"
                  option-label="number"
                  option-value="number"
                  :placeholder="
                    $t('maintenance_calculator.select_maintenance_number')
                  "
                  input-id="maintenance-number-select"
                  variant="filled"
                  :empty-message="' '"
                  :class="[
                    `
                    select-calculator
                    select-on${selectBackgroundColor}
                    p-inputwrapper-focus
                    maintenance-calculator__select
                  `,
                  ]"
                  :disabled="!selectedEngine && maintenanceNumbers.length === 0"
                />
              </div>
              <div
                v-if="maintenanceType === 'mileage'"
                class="maintenance-calculator__form-item maintenance-calculator__form-item--last"
              >
                <FloatLabel
                  variant="on"
                  :class="[
                    `maintenance-calculator__field floatlabel-on${selectBackgroundColor}`,
                  ]"
                >
                  <InputNumber
                    v-model="mileageValue"
                    :placeholder="
                      $t('maintenance_calculator.mileage_placeholder')
                    "
                    input-id="mileage"
                    class="maintenance-calculator__input"
                  />
                </FloatLabel>
              </div>
              <div v-if="showError" class="maintenance-calculator__error">
                {{ $t('maintenance_calculator.error_required_fields') }}
              </div>
            </div>
            <Button
              type="button"
              rounded
              :label="$t('maintenance_calculator.calculate_button')"
              :class="[
                'maintenance-calculator__button link-normal',
                backgroundColor === 'black' ? 'button-white' : 'button-black',
              ]"
              :disabled="!isFormValid"
              @click="calculateMaintenance"
            />
          </form>
          <div
            :class="[
              'maintenance-calculator__results',
              calculationResult
                ? 'maintenance-calculator__results--result'
                : '',
            ]"
          >
            <p class="h4 m-0 maintenance-calculator__results-title">
              {{ $t('maintenance_calculator.result_title') }}
            </p>
            <template v-if="calculationResult">
              <div class="maintenance-calculator__results-wrap">
                <div class="maintenance-calculator__results-top">
                  <div class="maintenance-calculator__media">
                    <NuxtImg
                      v-if="calculationResult?.image?.src"
                      class="maintenance-calculator__image"
                      :src="calculationResult?.image?.src"
                      :alt="calculationResult?.image?.alt"
                      :height="calculationResult?.image?.height"
                      :width="calculationResult?.image?.width"
                      loading="lazy"
                    />
                  </div>
                  <p class="m-0 h5 maintenance-calculator__car-name">
                    {{ calculationResult?.brand_name }}
                    {{ calculationResult?.model_name }}
                  </p>
                </div>
                <div class="maintenance-calculator__results-bottom">
                  <div class="maintenance-calculator__price-box">
                    <span
                      class="maintenance-calculator__price-label body-3-regular"
                    >
                      {{ $t('maintenance_calculator.estimated_price') }}
                    </span>
                    <span
                      class="maintenance-calculator__price-value body-1-bold"
                    >
                      {{ formatPrice(calculationResult.pricing.price) }}
                      {{ calculationResult.pricing.currency }}
                    </span>
                  </div>
                  <div class="maintenance-calculator__disclaimer label">
                    <CIcon
                      name="info-filled"
                      width="12"
                      height="13"
                      class="maintenance-calculator__disclaimer-icon"
                    />
                    <span>{{
                      $t('maintenance_calculator.price_disclaimer')
                    }}</span>
                  </div>
                </div>
              </div>
              <Buttons
                :background-color="selectBackgroundColor"
                class="maintenance-calculator__buttons"
                :buttons-prefix-class="'js-button-maintenance-calculator'"
                :maintenance-data="{
                  brand: calculationResult.brand_name,
                  model: calculationResult.model_name,
                  engine: getTitleById(engines, selectedEngine),
                  maintenanceType: maintenanceType,
                  maintenance:
                    maintenanceType === 'number'
                      ? selectedMaintenanceNumber
                      : null,
                  mileage: maintenanceType === 'mileage' ? mileageValue : null,
                }"
                :buttons="[
                  {
                    id: 1,
                    type: 'popup',
                    title: $t('maintenance_calculator.button'),
                    popup: 'maintenance_calculator',
                  },
                ]"
              />
            </template>
            <template v-else>
              <div class="maintenance-calculator__stub">
                <div class="maintenance-calculator__stub-top">
                  <div class="maintenance-calculator__stub-media">
                    <NuxtImg
                      src="/images/style/calculator-car.png"
                      :alt="$t('maintenance_calculator.title')"
                      height="138"
                      width="285"
                      class="maintenance-calculator__stub-image"
                      loading="lazy"
                    />
                  </div>
                  <p
                    class="m-0 maintenance-calculator__stub-placeholder body-1-regular"
                  >
                    {{ $t('maintenance_calculator.enter_parameters') }}
                  </p>
                </div>
                <div class="maintenance-calculator__stub-bottom">
                  <p
                    class="h6-mobile m-0 maintenance-calculator__stub-bottom-title"
                  >
                    {{ $t('maintenance_calculator.what_is_maintenance') }}
                  </p>
                  <p
                    class="m-0 maintenance-calculator__stub-bottom-description body-3-regular"
                  >
                    {{ $t('maintenance_calculator.what_is_maintenance_text') }}
                  </p>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
@use 'sass:math';
.maintenance-calculator {
  display: flex;
  flex-direction: column;
  gap: 56px;
  @include media-breakpoint-up(md) {
    gap: 80px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 80);
  }
  &__wrap {
    border-radius: 8px;
    @include media-breakpoint-up(md) {
      display: grid;
      grid-template-rows: min-content;
      grid-template-columns: repeat(2, 1fr);
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 8);
    }
    &--white {
      background-color: $gray;
    }
    &--black {
      background-color: rgba($surface-container-opacity, 0.5);
    }
    &--gray {
      background-color: $white;
    }
  }
  &__form {
    display: grid;
    grid-template-rows: min-content;
    gap: 32px;
    padding: 32px 16px;
    @include media-breakpoint-up(sm) {
      gap: 48px;
      padding: 48px 64px 94px;
    }
    @include media-breakpoint-up(md) {
      padding-bottom: 48px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(padding-top, 48);
      @include responsive-spacing(padding-bottom, 48);
      @include responsive-spacing(padding-left, 64);
      @include responsive-spacing(padding-right, 64);
      @include responsive-spacing(gap, 48);
    }
    &-title {
      text-align: center;
    }
    &-group {
      display: grid;
      grid-template-rows: min-content;
      gap: 24px;
      margin-top: 14px;
      @include media-breakpoint-up(sm) {
        column-gap: 16px;
        grid-template-columns: repeat(2, 1fr);
        row-gap: 40px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(margin-top, 14);
        @include responsive-spacing(row-gap, 40);
        @include responsive-spacing(column-gap, 16);
      }
    }
    &-item {
      grid-column: span 2;
      &--brand {
        @include media-breakpoint-up(sm) {
          grid-column: span 1;
        }
      }
      &--model {
        @include media-breakpoint-up(sm) {
          grid-column: span 1;
        }
      }
      &--radio {
        display: grid;
        gap: 20px;
        grid-template-rows: min-content;
        padding: 6px 0 0 4px;
        @include media-breakpoint-up(sm) {
          grid-template-columns: repeat(2, 1fr);
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(padding-top, 6);
          @include responsive-spacing(padding-left, 4);
          @include responsive-spacing(gap, 20);
        }
      }
      &--last {
        @include media-breakpoint-up(sm) {
          margin-top: -18px;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(margin-top, -14);
        }
      }
    }
  }
  &__input {
    width: 100%;
  }
  &__radio {
    align-items: center;
    display: grid;
    gap: 12px;
    grid-template-columns: 16px max-content;
    grid-template-rows: min-content;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 12);
      grid-template-columns: px-to-vw(16) max-content;
    }
    &-label {
      text-align: left;
    }
  }
  &__button {
    max-width: max-content;
    margin: 0 auto;
  }
  &__results {
    border-top: 1px solid $divider;
    display: grid;
    gap: 32px;
    grid-template-rows: min-content;
    padding: 32px 16px;
    @include media-breakpoint-up(sm) {
      gap: 64px;
      padding: 48px 64px;
    }
    @include media-breakpoint-up(md) {
      border-top: unset;
      border-left: 1px solid $divider;
      gap: 43px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-top-width, 1);
      @include responsive-spacing(padding-top, 48);
      @include responsive-spacing(padding-bottom, 48);
      @include responsive-spacing(padding-right, 64);
      @include responsive-spacing(padding-left, 64);
      @include responsive-spacing(gap, 43);
    }
    &--result {
      @include media-breakpoint-up(sm) {
        gap: 48px;
      }
      @include media-breakpoint-up(md) {
        gap: 48px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 48);
      }
    }
    &-title {
      text-align: center;
    }
    &-wrap {
      display: grid;
      gap: 24px;
      @include media-breakpoint-up(sm) {
        gap: 40px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 40);
      }
    }
    &-top {
      display: grid;
      gap: 8px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 8);
      }
    }
    &-bottom {
      display: grid;
      gap: 16px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 16);
      }
    }
  }
  &__buttons {
    justify-content: center;
    @include media-breakpoint-up(md) {
      margin-top: -24px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(margin-top, -24);
    }
  }
  &__disclaimer {
    border-top: 1px solid $divider;
    color: $on-surface-variant-2;
    display: grid;
    gap: 12px;
    grid-template-columns: 12px auto;
    padding-top: 16px;
    width: 100%;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 12);
      @include responsive-spacing(padding-top, 16);
      grid-template-columns: px-to-vw(12) auto;
    }
    &-icon {
      height: auto;
      width: 100%;
    }
  }
  &__stub {
    display: grid;
    grid-template-rows: min-content;
    gap: 40px;
    @include media-breakpoint-up(sm) {
      gap: 64px;
    }
    @include media-breakpoint-up(md) {
      gap: 43px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 43);
    }
    &-top {
      display: grid;
      grid-template-rows: min-content;
      gap: 8px;
      @include media-breakpoint-up(sm) {
        gap: 40px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 40);
      }
    }
    &-media {
      display: grid;
      grid-template-rows: min-content;
      justify-content: center;
    }
    &-image {
      height: auto;
      max-width: 100%;
    }
    &-placeholder {
      text-align: center;
      @include media-breakpoint-up(sm) {
        max-width: 69%;
        margin: 0 auto;
      }
      @include media-breakpoint-up(md) {
        max-width: unset;
      }
    }
    &-bottom {
      display: grid;
      gap: 16px;
      grid-template-rows: min-content;
      @include media-breakpoint-up(sm) {
        max-width: 88%;
      }
      @include media-breakpoint-up(md) {
        max-width: unset;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 16);
      }
    }
  }
  &__media {
    display: grid;
    grid-template-rows: min-content;
    justify-content: center;
  }
  &__image {
    height: auto;
    max-width: 100%;
    @include media-breakpoint-up(sm) {
      max-width: 310px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(max-width, 310);
    }
  }
  &__car-name {
    text-align: center;
  }
  &__price {
    &-box {
      display: grid;
      gap: 8px;
      text-align: center;
      @include media-breakpoint-up(sm) {
        align-items: center;
        gap: 24px;
        grid-template-columns: auto auto;
        justify-content: space-between;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 24);
      }
    }
  }
}
</style>
